import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useSplitEnabled } from '@bufferapp/features';

import { EmptyState, BufferLoading, QueueItems } from '~/shared-components';
import ComposerPopover from '~/composer-popover';
import ProfilesDisconnectedBanner from '~/profiles-disconnected-banner';
import getErrorBoundary from '~/web/components/ErrorBoundary';

import { setLastVisitedPage } from '~/utils/page-visits';
import { QUEUE_PAGE } from '~/constants';
import getQueryParamsFromURL from '~/queue/util/getQueryParams';
import { MastodonChannelsPromoBanner } from '../Banners/MastodonChannelsPromoBanner';
import { FollowBufferMastodon } from '../Banners/FollowBufferMastodon';
import QueuePausedBar from '../QueuePausedBar';
import RemindersBanner from '../RemindersBanner';
import { CleanUpPrompt } from '../CleanUpPrompt';

import { ConnectYoutubeChannelBanner } from '../Banners/connect-youtube';

const ErrorBoundary = getErrorBoundary(true);

const topBarContainerStyle = {
  display: 'flex',
};

const loadingContainerStyle = {
  width: '100%',
  height: '100%',
  textAlign: 'center',
  paddingTop: '5rem',
};

const QueuedPosts = ({
  showEmptyQueueMessage,
  loading,
  items,
  onComposerCreateSuccess,
  onRequeueClick,
  onDeleteConfirmClick,
  onEditClick,
  onEmptySlotClick,
  onCalendarClick,
  onCreatePostClick,
  onShareNowClick,
  onDropPost,
  onSwapPosts,
  showComposer,
  editMode,
  paused,
  draggingEnabled,
  onUnpauseClick,
  subprofiles,
  shouldDisplayDisconnectedBanner,
  isManager,
  hasFirstCommentFlip,
  hasPushNotifications,
  onConfirmDismissComposer,
  onSetRemindersClick,
  onCampaignTagClick,
  hasCampaignsFeature,
  shouldRenderCalendarButtons,
  shouldDisplaySingleSlots,
  shouldDisplayRemindersBanner,
  shouldDisplayTimezone,
  profileTimezone,
  onTimezoneClick,
  onAutoOpenComposer,
  composerType,
  showMastodonPromoBanner,
  isMastodonProfile,
}) => {
  const { search } = useLocation();
  const queryData = getQueryParamsFromURL(search);

  const shouldAutoOpenComposer = queryData?.shouldAutoOpenComposer;

  const { isEnabled: isYoutubeEnabled } = useSplitEnabled('youtube');

  useEffect(() => {
    setLastVisitedPage(QUEUE_PAGE);
    if (shouldAutoOpenComposer) {
      const selectedProfileIds = queryData?.selectedProfileIds || null;
      onAutoOpenComposer({
        selectedProfileIds,
      });
    }
  }, []);

  if (loading) {
    return (
      <div style={loadingContainerStyle}>
        <BufferLoading size={64} />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      {shouldDisplayDisconnectedBanner && <ProfilesDisconnectedBanner />}
      <CleanUpPrompt />
      {shouldDisplayRemindersBanner && (
        <RemindersBanner onSetRemindersClick={onSetRemindersClick} />
      )}
      {showMastodonPromoBanner && <MastodonChannelsPromoBanner />}
      {isMastodonProfile && <FollowBufferMastodon />}
      <div style={topBarContainerStyle}>
        {showComposer && !editMode && (
          <ComposerPopover
            onSave={onComposerCreateSuccess}
            shouldResetComposerData
            type={composerType}
            onConfirmDismissComposer={onConfirmDismissComposer}
          />
        )}
      </div>
      {!!paused && (
        <QueuePausedBar
          isManager={isManager}
          handleClickUnpause={onUnpauseClick}
        />
      )}
      {showEmptyQueueMessage && (
        <EmptyState
          title="It looks like you haven't got any posts in your queue!"
          subtitle="Click the box above to add a post to your queue :)"
          heroImg="https://s3.amazonaws.com/buffer-publish/images/fresh-queue%402x.png"
          heroImgSize={{ width: '229px', height: '196px' }}
        />
      )}
      {showComposer && editMode && (
        <ComposerPopover
          onSave={onComposerCreateSuccess}
          type={composerType}
          onConfirmDismissComposer={onConfirmDismissComposer}
        />
      )}
      {isYoutubeEnabled && <ConnectYoutubeChannelBanner />}
      <QueueItems
        items={items}
        subprofiles={subprofiles}
        onCalendarClick={onCalendarClick}
        onCreatePostClick={onCreatePostClick}
        onRequeueClick={onRequeueClick}
        onDeleteConfirmClick={onDeleteConfirmClick}
        onEditClick={onEditClick}
        onEmptySlotClick={onEmptySlotClick}
        onShareNowClick={onShareNowClick}
        onDropPost={onDropPost}
        onSwapPosts={onSwapPosts}
        draggable={draggingEnabled}
        hasFirstCommentFlip={hasFirstCommentFlip}
        hasPushNotifications={hasPushNotifications}
        onSetRemindersClick={onSetRemindersClick}
        onCampaignTagClick={onCampaignTagClick}
        hasCampaignsFeature={hasCampaignsFeature}
        shouldRenderCalendarButtons={shouldRenderCalendarButtons}
        timezoneItems={{
          shouldDisplayTimezone,
          profileTimezone,
          onTimezoneClick,
        }}
        pinned={!shouldDisplaySingleSlots}
      />
    </ErrorBoundary>
  );
};

QueuedPosts.propTypes = {
  loading: PropTypes.bool,
  moreToLoad: PropTypes.bool, // eslint-disable-line
  page: PropTypes.number, // eslint-disable-line
  items: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
    })
  ).isRequired,
  subprofiles: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
    })
  ),
  showEmptyQueueMessage: PropTypes.bool,
  onComposerCreateSuccess: PropTypes.func.isRequired,
  onConfirmDismissComposer: PropTypes.func.isRequired,
  onRequeueClick: PropTypes.func.isRequired,
  onDeleteConfirmClick: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired,
  onEmptySlotClick: PropTypes.func.isRequired,
  onShareNowClick: PropTypes.func.isRequired,
  onCampaignTagClick: PropTypes.func,
  onDropPost: PropTypes.func.isRequired,
  onSwapPosts: PropTypes.func.isRequired,
  onSetRemindersClick: PropTypes.func.isRequired,
  showComposer: PropTypes.bool,
  editMode: PropTypes.bool,
  paused: PropTypes.bool,
  draggingEnabled: PropTypes.bool,
  onUnpauseClick: PropTypes.func.isRequired,
  isManager: PropTypes.bool,
  hasPushNotifications: PropTypes.bool,
  shouldDisplayRemindersBanner: PropTypes.bool,
  shouldDisplayDisconnectedBanner: PropTypes.bool,
  hasFirstCommentFlip: PropTypes.bool,
  onCalendarClick: PropTypes.func.isRequired,
  onCreatePostClick: PropTypes.func.isRequired,
  hasCampaignsFeature: PropTypes.bool,
  shouldDisplaySingleSlots: PropTypes.bool,
  shouldRenderCalendarButtons: PropTypes.bool,
  shouldDisplayTimezone: PropTypes.bool,
  profileTimezone: PropTypes.string,
  onTimezoneClick: PropTypes.func.isRequired,
  onAutoOpenComposer: PropTypes.func.isRequired,
  composerType: PropTypes.string,
  showMastodonPromoBanner: PropTypes.bool,
  isMastodonProfile: PropTypes.bool,
};

QueuedPosts.defaultProps = {
  shouldDisplayTimezone: false,
  profileTimezone: '',
  loading: true,
  moreToLoad: false,
  page: 1,
  showComposer: false,
  showEmptyQueueMessage: false,
  editMode: false,
  paused: false,
  subprofiles: [],
  hasPushNotifications: true,
  shouldDisplayRemindersBanner: false,
  shouldDisplayDisconnectedBanner: false,
  hasFirstCommentFlip: false,
  draggingEnabled: false,
  isManager: false,
  hasCampaignsFeature: false,
  shouldRenderCalendarButtons: false,
  shouldDisplaySingleSlots: false,
  onCampaignTagClick: () => {},
  showMastodonPromoBanner: false,
  isMastodonProfile: false,
  composerType: 'queue',
};

export default QueuedPosts;
